import { IAttribute, Collection, IDatatype } from 'app/model/base/annotation';
import { Expression, ExpressionType, ScalarExpression, ExpressionFunction, expressionTypeArray } from './expression';
import { Owned } from 'app/model/base/named';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';

/**
 * Defines the behavior for expression resolvers.
 */
export interface IExpressionResolver {
    /**
     * Determines if the resolver can handle the given expression or not.
     * @param expression The expression to check against.
     * @param ignoreStart True to ignore the leading @ sign, false to include it in the check.
     * @returns whether the resolver can handle the expression or not.
     */
    isOfType(expression: string, ignoreStart?: boolean): boolean;

    /**
     * Parses and resolves the expression's category, return types, arguments (if any) and dependencies.
     * @param expression The expression syntax.
     * @param ignoreStart True to ignore the leading @ sign (nested level), false to include it in the check (top level).
     */
    resolve(expression: string, ignoreStart?: boolean): IExpressionResolution;

    /**
     * Determines if the expression is supported by the given entity.
     * @param entity The entity which has the field being parameterized.
     */
    checkApplicability(entity: Owned): IApplicability;
}

export interface IExpressionProperty {
    property: string;
    separator: string;
}

/**
 * Defines the expression is supported by the given entity.
 */
export interface IApplicability {
    isApplicable: boolean;
    error?: IResolutionError;
    treatAsWarning?: boolean;
}

/**
 * Defines the results expression resolution.
 */
export interface IExpressionResolution {
    /**
     * The source syntax.
     */
    source: string;

    /**
     * The resulting expression information.
     */
    expression?: Expression;

    /**
     * The expression's type.
     */
    returnType?: ExpressionType;

    /**
     * The expression's sub type in case of array.
     */
    returnSubType?: ExpressionType;

    /**
     * Indicates whether the expresion is valid and can be applied to the field or not.
     */
    isValid: boolean;

    /**
     * Lists dependencies of the expression such as activities or parameters.
     */
    dependencies?: Owned[];

    /**
     * The resolution error encountered.
     */
    error?: IResolutionError;

    /**
     * Indicates whether invalid resolution should be treated as a warning instead.
     */
    treatAsWarning?: boolean;
}

/**
 * Lists possible resolution errors.
 */
export enum ResolutionErrorKind {
    /**
     * The expression relies on a parameter than does not exist.
     */
    ParameterNotFound,

    /**
     * The expression indicates a pipeline or a dataset parameter but lack the parameter name. e.g. @dataset()
     */
    ParameterNotSpecified,

    /**
     * The expression's return type can't be applied to the field's type.
     */
    TypeMismatch,

    /**
     * The given syntax was not recongnized as an expression.
     */
    Unrecognized,

    /**
     * The type of an expressions is not recongnized (e.g. invalid parameter type).
     */
    UnrecognizedType,

    /**
     * The expression references a parameter which belongs to a different concept (e.g. dataset level expression uses a pipeline parameter or vice versa).
     */
    ParameterInaccessible,

    /**
     * Invalid expression syntax was detected.
     */
    InvalidSyntax,

    /**
     * A function being invoked in the expression is not registered.
     */
    FunctionNotFound,

    /**
     * The system variable being used is not registered.
     */
    SystemVariableNotFound,

    /**
     * The system variables being used are only useful in nested pipeline scenarios.
     */
    SystemVariableNestedPipelineWarning,

    /**
     * The trigger parameters being used are only useful in storage trigger scenarios
     */
    TriggerEventWarning,

    /**
     * Wrong argument count was given to a function inside the expression.
     */
    InvalidArgumentCount,

    /**
     * Could not find a function overload that supports the arguments given to it in the expression.
     */
    NoMatchingArguments,

    /**
     * The expression has tabs or carriage return or newline characters in it.
     */
    ExpressionContainsWhiteSpace,

    /**
     * An empty string was provided.
     */
    EmptyExpression,

    /**
     * The field being parameterized belongs to a concept that does not accept expressions.
     */
    UnsupportedConcept,

    /**
     * Only a single string literal argument is allowed inside an activity clause but something else was provided.
     */
    SingleStringLiteralError,

    /**
     * Missing activity name in @activity('').
     */
    MissingActivityName,

    /**
     * A property used in an expression was not found. E.g. activity('c1').output132. output132 is not recognized.
     */
    PropertyNotFound,

    /**
     * The expression goes into properties of an object which is primitive.
     */
    ExcesssNestedProperties,

    /**
     * Specifies when an object is primitive and therefore cannot use symbols (+, -, /, *)
     */
    UseBuiltInArithmetic,

    /**
     * The expresion tries to access the output of an activity that is not in its ancestry.
     */
    ActivityNotReferencable,

    /**
     * The expresion tries to access the output of an activity that is in its ancestry, but has no output.
     */
    ActivityNoOutput,

    /**
     * An items expression syntax was used in a place other than a for each activity contained activity.
     */
    ItemsSyntaxNotApplicable,

    /**
     * An empty @{} was found.
     */
    EmptyInterpolationExpression,

    /**
     * The subscript operator was applied to a non array property.
     */
    NotAnArray,

    /**
     * The expression is valid but can't be applied to the given entity.
     */
    ExpressionNotApplicable,

    /*
     * The expression contains a name of a variable that does not exist.
     */
    VariableNotReferencable,

    /*
     * The expression contains the keyword variable but nothing inside of it.
     */
    MissingVariableNameResolutionError,

    /*
     * The expression contains self referencing variable
     */
    SelfReferencingVariable,

    /**
     * The expression that attempts to use a variable syntax where it is not supported (e.g. datasets).
     */
    VariablesUnsupported
}

/**
 * Defines a parsing error.
 */
export interface IParsingError {
    /**
     * The kind of error the parser ran into.
     */
    error: ParsingErrorKind;

    /**
     * Where in the argument list the error was found.
     */
    startIndex: number;

    /**
     * Optional error ending index to aid in highlighting.
     */
    endIndex?: number;

    /**
     * An optional expression resolution (in case of expressions) which gives more context on the argument error.
     */
    resolution?: IExpressionResolution;

    /**
     * An option inner exception which could be useful for debugging.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    innerException?: any;
}

/**
 * Different kinds of errors the parser can report.
 */
export enum ParsingErrorKind {
    /**
     * A string literal lacks a closing single quote.
     */
    MissingClosingQuote,

    /**
     * An array literal lacks a closing bracket.
     */
    MissingClosingBracket,

    /**
     * An object literal lacks a closing brace.
     */
    MissingClosingBrace,

    /**
     * A property was accessed without a dot prefix.
     */
    MissingDot,

    /**
     * A JSON literal is invalid.
     */
    InvalidJsonSyntax,

    /**
     * There is an extra trailing comma after the last argument.
     */
    ExtraTrailingComma,

    /**
     * There is an extra trailing dot.
     */
    ExtraTrailingDot,

    /**
     * There is an extra trailing space.
     */
    ExtraTrailingSpace,

    /**
     * There is a missing comma between arguments.
     */
    MissingComma,

    /**
     * An argument was not recognized.
     */
    UnrecognizedToken,

    /**
     * Expected an opening paranthesis.
     */
    OpeningParanthesisExpected,

    /**
     * The closing paranthesis of an expression is missing.
     */
    MissingEnclosingParanthesis,

    /**
     * Extra closing paranthesis of an expression.
     */
    ExtraEnclosingParanthesis,

    /**
     * An extra closing bracket was encountered.
     */
    ExtraClosingBracket,

    /**
     * Invalid array property was encountered. e.g. obj.params[1]ddd
     */
    InvalidArrayProperty,

    /**
     * Using the ?. to specify null operator is lacking a dot.
     */
    NullSafeOperatorIncomplete
}

/**
 * Defines a resolution error.
 */
export interface IResolutionError {
    kind: ResolutionErrorKind;
    parsingError?: IParsingError;
    startIndex?: number;
    endIndex?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details?: any;
}

/**
 * Defines JSON object schemas.
 */
export interface IObjectSchema {
    expressionType: ExpressionType;
    subType?: ExpressionType;
    schema?: {
        [key: string]: IObjectSchema
    };
}

/**
 * Builds a successful expression resolution object.
 * @param source The expression source code.
 * @param expression The expression object.
 * @param dependencies Optional dependencies.
 */
export function successfulExpressionResolution(source: string, expression: Expression, dependencies?: Owned[]): IExpressionResolution {
    let returnType: ExpressionType;
    let returnSubType: ExpressionType;

    if (expression instanceof ScalarExpression) {
        returnType = (expression as ScalarExpression).returnType;
        returnSubType = (expression as ScalarExpression).returnSubType;
    } else {
        returnType = (expression as ExpressionFunction).specification.returnType;
        returnSubType = (expression as ExpressionFunction).specification.subReturnType;
    }

    return {
        isValid: true, source, expression, returnType, returnSubType, dependencies
    };
}

/**
 * Builds a failed expression resolution object.
 * @param source The expression source code.
 * @param error The resolution error encountered.
 * @param expression The expression object.
 * @param dependencies Optional dependencies.
 */
export function failedExpressionResolution(source: string, error: IResolutionError, expression?: Expression, dependencies?: Owned[], treatAsWarning?: boolean): IExpressionResolution {
    let returnType: ExpressionType;
    let returnSubType: ExpressionType;

    if (expression) {
        if (expression instanceof ScalarExpression) {
            returnType = (expression as ScalarExpression).returnType;
            returnSubType = (expression as ScalarExpression).returnSubType;
        } else {
            returnType = (expression as ExpressionFunction).specification.returnType;
            returnSubType = (expression as ExpressionFunction).specification.subReturnType;
        }
    }

    return {
        isValid: false, source, error, expression, returnType, returnSubType, dependencies, treatAsWarning
    };
}

/**
 * Determines whether the expression's type can be applied to the field it is being used to parameterize.
 * @param field The field being parameterized.
 * @param expressionType The expression's return type.
 * @param expressionSubType The expression's sub return type (in case of arrays).
 */
export function matchExpressionTypeToFieldType(field: IAttribute, expressionType: ExpressionType, entity?: Owned, expressionSubType?: ExpressionType): boolean {
    const acceptedTypes = getAcceptsByConfig(field, entity);
    if (!arrayIsNullOrEmpty(acceptedTypes)) {
        return acceptedTypes.includes(expressionType) || acceptedTypes.includes('Any') || expressionType === 'Any';
    }

    const stringMatch = (eType: ExpressionType): boolean => ['String', 'Any'].includes(eType);
    const numberMatch = (eType: ExpressionType): boolean => ['Int', 'Float', 'Any'].includes(eType);
    const booleanMatch = (eType: ExpressionType): boolean => ['Bool', 'Any'].includes(eType);

    const match = (fieldType: IDatatype, eType: ExpressionType): boolean => {
        switch (fieldType) {
            case String.name:
                return stringMatch(eType);
            case Number.name:
                return numberMatch(eType);
            case Boolean.name:
                return booleanMatch(eType);
            default:
                return true;
        }
    };

    let doesMatch = true;

    if (field.collection === Collection.Array) {
        expressionSubType = expressionSubType || 'Any';

        // in case of non primitive, sub-type does not matter, we consider the field to be an array<any>
        doesMatch = expressionType === 'Any' || ((field.primitive ? match(field.datatype(), expressionSubType) : true) && expressionType === 'Array');
    } else {
        doesMatch = field.primitive ? match(field.datatype(), expressionType) : ['Object', 'Any'].includes(expressionType);
    }

    return doesMatch;
}

/**
 * Determines whether the expression's type can be applied to the field it is being used to parameterize.
 * @param field The field being parameterized.
 * @param expressionType The expression's return type.
 * @param expressionSubType The expression's sub return type (in case of arrays).
 */
 export function suggestValidType(field: IAttribute): string {
    if (matchExpressionTypeToFieldType(field, 'String')) {
        return 'string';
    }
    if (matchExpressionTypeToFieldType(field, 'Int')) {
        return 'int';
    }
    if (matchExpressionTypeToFieldType(field, 'Float')) {
        return 'float';
    }
    if (matchExpressionTypeToFieldType(field, 'Bool')) {
        return 'bool';
    }
    if (matchExpressionTypeToFieldType(field, 'Array')) {
        return 'array';
    }
    if (matchExpressionTypeToFieldType(field, 'Object')) {
        return 'object';
    }
    if (matchExpressionTypeToFieldType(field, 'SecureString')) {
        return 'securestring';
    }
    return 'string';
}

/**
 * Case insensitive conversion from a string expression type to an ExpressionType.
 * @param rawType Type to convert to an ExpressionType
 * @returns ExpressionType value converted from the argument if the conversion succeeds.
 *  undefined otherwise.
 */
export function convertStringToExpressionType(rawType: string): ExpressionType {
    if (stringIsNullOrEmpty(rawType)) {
        return undefined;
    }

    const typeInlowerCase = rawType.toLowerCase();
    const supportedType = expressionTypeArray.find((supportedType) => {
        return supportedType.toLowerCase() === typeInlowerCase;
    });

    return supportedType ? (supportedType as ExpressionType) : undefined;
}


export function getAcceptsByConfig(field: IAttribute, entity?: Owned): ExpressionType[] {
    if (entity && field.dynamicAccepts) {
        const acceptedTypes = field.dynamicAccepts(entity);
        if (acceptedTypes?.length > 0) {
            return acceptedTypes;
        }
    }

    if (field.accepts?.length > 0) {
        return field.accepts;
    }
    return null;
}
